import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, map } from 'rxjs';
import { Session } from '../models/session';
import { User } from '../models/user';
import { SpaceRepository } from '../state/space.repository';
import { PresenceProvider, PresenceRoomInfo } from './presence-provider';
import { PresenceRoomOwner, SpacePresenceRoomsService } from './space-presence-rooms.service';
import { UserService } from './user.service';

/**
 * Tracks which spaces rendered in the sidebar switcher have a call in progress.
 *
 * Scoping mirrors the spaces list page: hosts see a call anywhere in the space, everyone else only
 * sees a call in the breakout room they're assigned to, so the two surfaces can never disagree.
 * The space the user is currently in is treated like any other and keeps its indicator.
 */
@Injectable({ providedIn: 'root' })
export class SwitcherCallPresenceService {
  private readonly subscriptions = new Map<string, Subscription>();
  private readonly spacesInCall = new Set<string>();
  private readonly _spacesInCall$ = new BehaviorSubject<ReadonlySet<string>>(new Set());

  /** Ids of tracked spaces with a live call. Emits a fresh Set on every change. */
  readonly spacesInCall$: Observable<ReadonlySet<string>> = this._spacesInCall$.asObservable();

  constructor(
    private presenceProvider: PresenceProvider,
    private spacePresenceRooms: SpacePresenceRoomsService,
    private spaceRepository: SpaceRepository,
    private userService: UserService,
  ) {}

  /**
   * Start reporting call activity for these spaces. Idempotent and additive, so the switcher can
   * call it with its full current list after every page it loads.
   */
  track(spaces: Session[]): void {
    const user = this.userService.user.getValue()?.user;
    const spaceById = new Map(spaces.map((space) => [space._id, space]));
    const presenceInfos = this.resolveViewerRooms(spaces, user);
    if (!presenceInfos.length) {
      return;
    }

    // Acquire for the whole list, not only spaces we haven't subscribed to yet — that is what lets
    // a room handed back after a failed join be picked up again on the next page load.
    this.spacePresenceRooms.acquire(PresenceRoomOwner.SWITCHER, presenceInfos);

    for (const { spaceId, breakoutRoomId } of presenceInfos) {
      const space = spaceById.get(spaceId);
      if (!space || this.subscriptions.has(spaceId)) {
        continue;
      }

      // Hosts read across every room in the space, everyone else only their own.
      const readRoomId = this.spaceRepository.hasSpaceHostPermissions(user, space)
        ? undefined
        : breakoutRoomId;

      this.subscriptions.set(
        spaceId,
        this.presenceProvider
          // getRtcPresence must be false: with the default, this switches to the LiveKit participant
          // list of the room *we* are in whenever our own call is connected, which would smear our
          // current call's participants across every other space's row.
          .getCallPresenceActivity(spaceId, readRoomId, false)
          .pipe(
            map((usersInCall) => usersInCall.size > 0),
            distinctUntilChanged(),
          )
          .subscribe((inCall) => this.applyCallState(spaceId, inCall)),
      );
    }
  }

  /** Drop every subscription and release the presence rooms this service holds. */
  untrackAll(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.clear();
    this.spacesInCall.clear();
    this._spacesInCall$.next(new Set());
    this.spacePresenceRooms.releaseAll(PresenceRoomOwner.SWITCHER);
  }

  /**
   * The room to join per space, skipping spaces where the viewer is neither a member nor an admin
   * acting as host — there is no room to attribute them to. Mirrors
   * SpacesViewComponent.extractCurrentBreakoutRoomIds.
   */
  private resolveViewerRooms(spaces: Session[], user: User | undefined): PresenceRoomInfo[] {
    const presenceInfos: PresenceRoomInfo[] = [];

    for (const space of spaces) {
      const member = space.users?.find((spaceUser) => spaceUser._id === user?._id);
      if (!member && !this.spaceRepository.hasSpaceAdminPermissions(user, space)) {
        continue;
      }

      presenceInfos.push(
        this.presenceProvider.constructPresenceInfoObject(space._id, member?.roomId),
      );
    }

    return presenceInfos;
  }

  private applyCallState(spaceId: string, inCall: boolean): void {
    if (inCall) {
      this.spacesInCall.add(spaceId);
    } else if (!this.spacesInCall.delete(spaceId)) {
      return;
    }

    this._spacesInCall$.next(new Set(this.spacesInCall));
  }
}
